import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './UsersSuggestion.css';
import { useNavigate } from "./Router";
import { Button, Alert } from 'reactstrap';
import { FaChevronLeft, FaChevronRight, FaUserPlus } from 'react-icons/fa';

const UsersSuggestion = ({onChange}) => {
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);

    const [users, setUsers] = useState([]);
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldDisplay, setShouldDisplay] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    const goUser = (user_id) => {
        navigate("/user/" + user_id);
    };

    const fetchSugg = async () => {
        setIsLoading(true);
        try {
            const ps = await sdk.models.PeupleUser.getSuggestions({});
            console.log("Users suggestions:", ps);
            setUsers(ps || []);
            setErrors(null);
        } catch (err) {
            console.error("Erreur lors du chargement des suggestions:", err);
            setErrors("Une erreur s'est produite lors du chargement des suggestions.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFollow = async (userId, event) => {
        event.stopPropagation();
        try {
            await sdk.models.Followers.create({
                following_id: userId,
                active: 1,
                time: Math.floor(Date.now() / 1000),
            });

            // Supprime l'utilisateur de la liste des suggestions
            setUsers((prevUsers) => prevUsers.filter((user) => user.user_id !== userId));

            // Appeler le callback onChange si défini
            if (typeof onChange === "function") onChange();
        } catch (err) {
            console.error("Erreur lors de l'action 'Suivre':", err);
            setErrors("Une erreur s'est produite lors de l'action 'Suivre'. Veuillez réessayer.");
        }
    };

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        if (shouldDisplay) {
            fetchSugg();
        }
    }, [shouldDisplay]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            // Vérifier initialement si on a besoin des flèches
            handleScroll();
        }
        
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [users]);

    // Ne pas afficher si pas de suggestions ou si ne doit pas être affiché
    if (!shouldDisplay || (!isLoading && users.length === 0)) return null;

    return (
        <div className="pqc-users-sugg-container">
            <div className="pqc-users-sugg-header">
                <h2 className="pqc-users-sugg-title">Suggestions de personnes</h2>
                <Button color="link" onClick={fetchSugg} className="pqc-users-sugg-refresh-btn">
                    Actualiser
                </Button>
            </div>
            
            {errors && (
                <Alert color="danger" className="pqc-users-sugg-error">
                    {errors}
                </Alert>
            )}
            
            {isLoading ? (
                <div className="pqc-users-sugg-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            ) : (
                <div className="pqc-users-sugg-scroll-container-wrapper">
                    {showLeftArrow && (
                        <div 
                            className="pqc-users-sugg-scroll-arrow pqc-users-sugg-scroll-left"
                            onClick={scrollLeft}
                            aria-label="Défiler vers la gauche"
                        >
                            <FaChevronLeft />
                        </div>
                    )}
                    
                    <div 
                        className="pqc-users-sugg-scroll-container" 
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                    >
                        {users.map((user) => (
                            <div 
                                key={user.user_id}
                                className="pqc-users-sugg-card"
                            >
                                <div 
                                    className="pqc-users-sugg-avatar-wrapper"
                                    onClick={() => goUser(user.user_id)}
                                >
                                    <img
                                        src={`https://peupleqc.com/s/peupleqc-upload/${user.avatar}`}
                                        alt={`${user.name || 'Utilisateur'} avatar`}
                                        className="pqc-users-sugg-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg";
                                        }}
                                    />
                                </div>
                                <div className="pqc-users-sugg-info">
                                    <div 
                                        className="pqc-users-sugg-info-top"
                                        onClick={() => goUser(user.user_id)}
                                    >
                                        <h3 
                                            className="pqc-users-sugg-username"
                                            title={user.name || 'Utilisateur'}
                                        >
                                            {user.name || 'Utilisateur'}
                                        </h3>
                                        
                                        <div className="pqc-users-sugg-badges">
                                            {user.user_id === 2 && (
                                                <span className="pqc-users-sugg-badge founder">Fondateur</span>
                                            )}
                                            {user.user_id === 1435 && (
                                                <span className="pqc-users-sugg-badge co-founder">Co-Fondateur</span>
                                            )}
                                            {user.same_city && (
                                                <span className="pqc-users-sugg-badge same-city">Même ville</span>
                                            )}
                                        </div>
                                        
                                        {user.followers_count > 0 && (
                                            <small className="pqc-users-sugg-stats">
                                                {user.followers_count} abonné{user.followers_count > 1 ? 's' : ''}
                                            </small>
                                        )}
                                    </div>
                                    
                                    {/* Bouton Suivre avec stopPropagation pour éviter la navigation */}
                                    <button
                                        className="pqc-users-sugg-follow-btn"
                                        onClick={(e) => handleFollow(user.user_id, e)}
                                    >
                                        <FaUserPlus /> Suivre
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {showRightArrow && (
                        <div 
                            className="pqc-users-sugg-scroll-arrow pqc-users-sugg-scroll-right"
                            onClick={scrollRight}
                            aria-label="Défiler vers la droite"
                        >
                            <FaChevronRight />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { UsersSuggestion };